import { Container, Graphics, Text } from "pixi.js"
import { EmberMemberStatus, EntityId } from "game-common/models"
import { TextRenderer } from "../text/text_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { SLOT_W } from "./ember_roster_card"

// THE STATUS PILL ROW: one chip per camp member, in the lane directly above the roster strip, saying
// what that member is doing.
//
// It is the lord's fire warning generalised. That pill already answered the one question the strip
// could not -- "is the fire out?" -- for the one card that had an answer; everybody else's card
// showed health, hunger and a face, and nothing at all about whether they were working, waiting on an
// order, or three seconds from walking out of the camp. The pills are drawn in the SAME box, at the
// same height, on the same baseline as the fire warning, so the row reads as one line of chips rather
// than as two different ideas stacked on top of each other.
//
// ONE PILL PER MEMBER, EVER. The server picks which of a member's several true states it names (see
// campaigns/ember/member_status.ts); this file is only the picture. That split is deliberate: the
// precedence is a game-design decision with tests on it, and the client should not be able to
// disagree with it.
//
// It costs nothing when there is nothing to say: a member whose status the server could not name has
// no pill at all, and the lord has none because his pill is the fire's.

// The chip's box. The same height the fire warning uses, so a camp whose fire has gone out shows that
// alarm level with the rest of the row rather than floating above it.
export const STATUS_PILL_H = 24

// Air on each side of a chip. The cards are one SLOT_W apart, so a chip drawn a full card wide
// touches both of its neighbours and the row reads as one long bar with words in it -- which is
// exactly what it looked like the first time a camp had two members in trouble at once. Inset by
// this, every chip has twice it between itself and the next.
export const STATUS_PILL_GAP = 3
export const STATUS_PILL_W = SLOT_W - STATUS_PILL_GAP * 2

// Clear air inside the chip, and how far a label may be squeezed to stay inside it. Two labels
// ("Defending", and "Needs job" in the alarm's bold) are a hair wider than the chip at the fire
// warning's size; shrinking those two by a few percent is invisible, where clipping them is not, and
// it beats inventing a shorter word for one of the states purely because of the font.
const PILL_PAD_X = 4
const MIN_TEXT_SCALE = 0.8

// How a chip is drawn, and whether it breathes. ONLY AN ALARM IS FILLED:
//
//   alarm  solid fill, pale text, PULSING -- the player has to do something or the camp loses this
//          member (it is stuck, or starving, or standing there waiting to be given a job)
//   warn   dark plate, tinted outline and light text in a warm colour, still -- worth knowing,
//          nothing is being lost yet
//   busy   the same dark plate and light text, in the member's role colour -- it is working
//
// The fill AND the bold are the alarm. A row of six filled, bold chips is six things shouting, which
// is no louder than one and tells the eye nothing about which card to look at; six light chips on dark
// plates with one solid red among them is a camp at work with one problem in it, read at a glance and
// without reading a word.
export type StatusTone = "alarm" | "warn" | "busy"

// The alarm/warning colours are the fire warning's own, so "red chip in the roster lane" keeps
// meaning exactly one thing. The working colours are the ROLE colours the cards and minimap dots
// already use, which is what lets the row be scanned by colour before it is read.
const ALARM = 0xff5959
const WARN = 0xffbd45
const FORAGER = 0x7be08a
const DEFENDER = 0xff8a5c
const FOLLOWING = 0x2bc4a8
// Ember gold: a visitor is not one of the camp yet, and their chips say so in the colour of the fire
// that drew them in (matching their card's patience ring).
const VISITOR = 0xffb648

const PILL_TEXT = 0xfff3e0
const PLATE_FILL = 0x10140f
const PLATE_ALPHA = 0.9

export interface StatusPillStyle {
    // Kept to about nine characters: the chip is one card wide (70px) and the label is drawn at the
    // same size as the fire warning's, so a longer phrase either shrinks below reading size or runs
    // off both ends of its own pill. Every label below has been counted.
    label: string
    tone: StatusTone
    color: number
}

export const STATUS_PILLS: Record<EmberMemberStatus, StatusPillStyle> = {
    // It has stopped, and will not start again until this player answers. The question mark is the
    // whole point: it is addressed to the reader.
    awaitingOrders: { label: "Orders?", tone: "alarm", color: ALARM },
    // Deliberately phrased like "No fire!", the alarm beside it: both are "the camp is out of
    // something and that is about to cost you somebody".
    needsFood: { label: "No food!", tone: "alarm", color: ALARM },
    hungry: { label: "Hungry", tone: "warn", color: WARN },
    idle: { label: "Idle", tone: "warn", color: WARN },
    fighting: { label: "Fighting", tone: "busy", color: DEFENDER },
    holding: { label: "Holding", tone: "busy", color: DEFENDER },
    defending: { label: "Defending", tone: "busy", color: DEFENDER },
    following: { label: "Following", tone: "busy", color: FOLLOWING },
    foraging: { label: "Foraging", tone: "busy", color: FORAGER },
    repairing: { label: "Repairing", tone: "busy", color: FORAGER },
    feedingFire: { label: "Fire duty", tone: "busy", color: FORAGER },
    hauling: { label: "Hauling", tone: "busy", color: FORAGER },
    wantsFood: { label: "Hungry", tone: "warn", color: VISITOR },
    // The one state the camp cannot resolve on its own: they have been paid and are standing at the
    // fire with nothing to do until a role is picked for them.
    needsJob: { label: "Needs job", tone: "alarm", color: VISITOR },
}

export const statusPillStyle = (status?: EmberMemberStatus): StatusPillStyle | undefined =>
    status ? STATUS_PILLS[status] : undefined

// Draw a chip's plate. Shared with the fire warning (see ember_roster.ts), which is the one pill that
// predates this row -- so there is exactly one place the shape of a pill is decided.
export const drawPillPlate = (plate: Graphics, color: number, tone: StatusTone) => {
    plate.clear()
    if (tone === "alarm") {
        plate.beginFill(color, 1)
    } else {
        plate.beginFill(PLATE_FILL, PLATE_ALPHA)
        plate.lineStyle(1, color, 0.55)
    }
    plate.drawRoundedRect(0, 0, STATUS_PILL_W, STATUS_PILL_H, STATUS_PILL_H / 2)
    plate.endFill()
}

// Centre a label in a chip, shrinking it to fit if the word is longer than the chip is wide. Shared
// with the fire warning, which is laid out in the same box.
export const fitPillText = (text: Text) => {
    const room = STATUS_PILL_W - PILL_PAD_X * 2
    text.scale.set(1)
    if (text.width > room) {
        text.scale.set(Math.max(MIN_TEXT_SCALE, room / text.width))
    }
    text.position.set(
        Math.round((STATUS_PILL_W - text.width) / 2),
        Math.round((STATUS_PILL_H - text.height) / 2),
    )
}

/** What the row is told about one member each time it is laid out. */
export interface StatusRowEntry {
    entityId: EntityId
    status?: EmberMemberStatus
    /** The resting x of that member's card, in the strip's own space. */
    x: number
}

interface Pill {
    // Placed and pulsed as one. The label is the plate's SIBLING, not its child: only the plate's
    // vector shape is cached (see paint), because a cached bake can come out without its Text.
    root: Container
    plate: Graphics
    text: Text
    status: EmberMemberStatus
    tone: StatusTone
}

export class RosterStatusRow extends Container {
    private pills: Map<EntityId, Pill> = new Map()

    // Lay the row out from the cards it belongs to. Called from the roster's own layout(), so the
    // chips move with the strip -- including the sideways shove a visitor's gap gives the household.
    //
    // Everything is diffed rather than rebuilt: a Text is a rasterised texture, the roster ticks twice
    // a second, and a member at its post holds the same status for minutes at a time.
    sync = (entries: StatusRowEntry[]) => {
        const seen = new Set<EntityId>()

        entries.forEach(entry => {
            const style = statusPillStyle(entry.status)
            if (!style) {
                // Nothing to say about this member. Any chip it had goes, rather than being left
                // showing the last thing that was true about it.
                this.forget(entry.entityId)
                return
            }
            seen.add(entry.entityId)
            let pill = this.pills.get(entry.entityId)
            if (!pill) {
                const root = new Container()
                const plate = new Graphics()
                // Weight is set per tone in `paint` (bold is the alarm's, not everybody's).
                const text = new TextRenderer().role("caption").strokeThickness(0).render("") as Text
                root.addChild(plate, text)
                this.addChild(root)
                pill = { root, plate, text, status: entry.status!, tone: style.tone }
                this.pills.set(entry.entityId, pill)
                this.paint(pill, entry.status!, style)
            } else if (pill.status !== entry.status) {
                this.paint(pill, entry.status!, style)
            }
            pill.root.x = entry.x + STATUS_PILL_GAP
        })

        Array.from(this.pills.keys()).forEach(entityId => {
            if (!seen.has(entityId)) {
                this.forget(entityId)
            }
        })
    }

    // The breath the alarm chips take, driven by the roster's own pulse clock so every alarm in the
    // HUD -- these, the fire warning, a starving member's hunger bar -- rises and falls together.
    // Opacity only: a chip that moved or scaled would drag the eye off the card it belongs to.
    pulse = (alpha: number) => {
        this.pills.forEach(pill => {
            pill.root.alpha = pill.tone === "alarm" ? alpha : 1
        })
    }

    forget = (entityId: EntityId) => {
        const pill = this.pills.get(entityId)
        if (!pill) {
            return
        }
        this.pills.delete(entityId)
        this.removeChild(pill.root)
        // Textures first, then the rest: a Text owns a canvas-backed texture pixi only reclaims on an
        // explicit destroy, and destroying it detaches it, so it is gone from the root by the time
        // the root goes (no double destroy).
        disposeTextTextures(pill.root)
        pill.root.destroy({ children: true })
    }

    forgetAll = () => {
        Array.from(this.pills.keys()).forEach(this.forget)
    }

    // --- for tests and the browser console
    labelFor = (entityId: EntityId): string | undefined => this.pills.get(entityId)?.text.text
    count = (): number => this.pills.size

    private paint = (pill: Pill, status: EmberMemberStatus, style: StatusPillStyle) => {
        pill.plate.cacheAsBitmap = false
        pill.status = status
        pill.tone = style.tone
        drawPillPlate(pill.plate, style.color, style.tone)
        pill.text.text = style.label
        // Pale ink on the filled alarm, the chip's own colour on every dark one -- the same two
        // treatments the HUD's filled and outlined buttons use.
        pill.text.tint = style.tone === "alarm" ? PILL_TEXT : style.color
        // BOLD IS THE ALARM'S, like the fill. A whole row set in bold is a row of headlines, and the
        // eye then has to read every one of them to find the one that matters; the working chips are
        // set light so they can be skipped.
        pill.text.style.fontWeight = style.tone === "alarm" ? "bolder" : "normal"
        fitPillText(pill.text)
        pill.root.alpha = 1
        // The shape is static until status changes; the alarm only pulses the root's alpha.
        pill.plate.cacheAsBitmap = true
    }
}
